import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MultipleChoice, MatchQuestion, ArrangeQuestion } from '../QuestionTypes';
import { useQuizState } from '../../hooks/useQuizState';
import { QuizLoading, QuizError, NoQuestions } from './QuizStatus';
import { QuizHeader } from './QuizHeader';
import { QuizNavigation } from './QuizNavigation';
import useAnswerValidation from '../../hooks/useAnswerValidation';
import './Quiz.css';

const Quiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  
  // Use custom hooks for state management
  const {
    quizData,
    currentQuestion,
    currentQuestionIndex,
    score,
    showScore,
    userAnswers,
    isLoading,
    error,
    isAnswerChecked,
    handleAnswer,
    handleCheckAnswer,
    handleNext,
    setShowScore
  } = useQuizState(quizId);
  
  // Use answer validation hook with current question's answer
  const { isAnswerCorrect: validateAnswer } = useAnswerValidation(currentQuestion, userAnswers);
  const showFeedback = isAnswerChecked && currentQuestion;
  
  // Check if current answer is correct
  const currentAnswerIsCorrect = React.useMemo(() => {
    if (!currentQuestion || !userAnswers[currentQuestion.id]) return false;
    return validateAnswer();
  }, [currentQuestion, userAnswers, validateAnswer]);
  
  // Handler functions
  const handleBack = () => navigate('/');
  const handleFinish = () => setShowScore(true);
  const handleRetry = () => window.location.reload();
  
  // Early returns for different states
  if (isLoading) return <QuizLoading />;
  if (error) return <QuizError error={error} onRetry={handleRetry} onBack={handleBack} />;
  if (!quizData || !quizData.questions?.length) {
    return <NoQuestions onBack={handleBack} message="No questions available for this quiz." />;
  }
  if (!currentQuestion) return <NoQuestions onBack={handleBack} message="Question not found." />;

  // Helper function to get feedback based on percentage
  const getFeedback = (percentage) => {
    if (percentage >= 70) {
      return {
        emoji: '🎉',
        message: 'Great job! You passed with flying colors!'
      };
    } else if (percentage >= 50) {
      return {
        emoji: '👍',
        message: 'Good effort! You\'re on the right track!'
      };
    }
    return {
      emoji: '💪',
      message: 'Keep practicing! You\'ll do better next time!'
    };
  };

  // Render the score screen if quiz is completed
  if (showScore) {
    const totalQuestions = quizData.questions.length;
    const percentage = Math.round((score / totalQuestions) * 100);
    const feedback = getFeedback(percentage);
    
    return (
      <div className="quiz-container">
        <div className="score-section">
          <h3>Quiz Completed!</h3>
          <p>You've completed the quiz. Here's your result:</p>
          
          <div className="score-display">
            {score} / {totalQuestions}
            <div style={{
              fontSize: '1rem',
              fontWeight: 'normal',
              color: '#6c757d',
              marginTop: '0.5rem'
            }}>
              ({percentage}%)
            </div>
          </div>
          
          <p>
            {feedback.emoji} {feedback.message}
          </p>
          
          <button 
            onClick={handleBack} 
            className="retry-button"
            aria-label="Back to quizzes"
          >
            Back to Quizzes
          </button>
        </div>
      </div>
    );
  }

  // Render the main quiz interface
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="quiz-container">
        <div className="quiz-content">
          <QuizHeader 
            title={quizData.title}
            image={quizData.image || null}
            onBack={handleBack}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={quizData.questions.length}
            score={score}
          />
          
          <div className="question-section">
            <div className={`question-text ${currentQuestion.image ? '' : 'large'}`}>
              {currentQuestion.text}
            </div>
            {currentQuestion.image && (
              <div className="question-image-container">
                <img 
                  src={currentQuestion.image} 
                  alt="" 
                  className="question-image"
                  aria-hidden="true"
                />
              </div>
            )}
            {showFeedback && (
              <div className={`feedback ${currentAnswerIsCorrect ? 'correct' : 'incorrect'}`}>
                {currentAnswerIsCorrect 
                  ? '✅ Correct! Well done!' 
                  : '❌ Incorrect. Try again!'}
              </div>
            )}
            
            <div className="answer-section">
              {(currentQuestion.type === 'multiple') && (
                <MultipleChoice
                  question={currentQuestion}
                  selectedAnswers={userAnswers[currentQuestion.id] || []}
                  onAnswerChange={(answers) => handleAnswer(currentQuestion.id, answers)}
                  isSubmitted={isAnswerChecked}
                  correctAnswers={currentQuestion.correctAnswers}
                />
              )}
              
              {currentQuestion.type === 'match' && (
                <MatchQuestion
                  question={currentQuestion}
                  onMatch={(matches) => handleAnswer(currentQuestion.id, matches)}
                  selectedAnswers={userAnswers[currentQuestion.id] || []}
                  isSubmitted={isAnswerChecked}
                  correctAnswers={currentQuestion.correctMatches}
                />
              )}
              
              {currentQuestion.type === 'arrange' && (
                <ArrangeQuestion
                  question={currentQuestion}
                  onArrange={(items) => handleAnswer(currentQuestion.id, items)}
                  items={userAnswers[currentQuestion.id] || currentQuestion.items}
                  isSubmitted={isAnswerChecked}
                  correctOrder={currentQuestion.correctOrder}
                />
              )}
            </div>
            
            <QuizNavigation
              isAnswerChecked={isAnswerChecked}
              hasAnswer={!!userAnswers[currentQuestion.id]}
              isLastQuestion={currentQuestionIndex === quizData.questions.length - 1}
              onCheckAnswer={handleCheckAnswer}
              onNext={handleNext}
              onFinish={handleFinish}
            />
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default Quiz;
